// src/presentation/pages/ClinicalExamination/Step2NeurologicalHistory.tsx
import React from "react";
import {
  PriorNeurologicalDiagnoses,
  CerebrovascularHistory,
  HeadInjury,
  CardiovascularRiskFactors,
  FamilyHistory,
  OnsetPattern,
} from "./sections";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import type { NeurologicalHistoryFormData } from "./types";

interface Step2NeurologicalHistoryProps {
  formData: NeurologicalHistoryFormData;
  onFormDataChange: (data: NeurologicalHistoryFormData) => void;
  onSkip?: () => void;
}

const Step2NeurologicalHistory: React.FC<Step2NeurologicalHistoryProps> = ({
  formData,
  onFormDataChange,
  onSkip,
}) => {
  const { t } = useTranslation("patients");
  const updateFormData = <K extends keyof NeurologicalHistoryFormData>(
    key: K,
    value: NeurologicalHistoryFormData[K],
  ) => {
    onFormDataChange({ ...formData, [key]: value });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col">
        <p className="text-[#5F646F] text-sm"> {t("clinicalSections.step2.stepLabel")}</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {t("clinicalSections.step2.title")}
            </h3>
            <div className="bg-[#D4DAEB] p-2 rounded">
              <p className="">{t("clinicalSections.step2.answeredByCaregiver")}</p>
            </div>
          </div>
          {/* {onSkip && ( */}
          <Button
            variant="link"
            onClick={onSkip}
            className="hover:text-primary cursor-pointer"
          >
            {t("clinicalSections.step2.skip")}
          </Button>
          {/* )} */}
        </div>
      </div>

      <PriorNeurologicalDiagnoses
        value={formData.hasNeurologicalDiagnosis}
        condition={formData.neurologicalCondition}
        yearOfDiagnosis={formData.neurologicalDiagnosisYear}
        onChange={(value) => updateFormData("hasNeurologicalDiagnosis", value)}
        onConditionChange={(value) =>
          updateFormData("neurologicalCondition", value)
        }
        onYearChange={(value) =>
          updateFormData("neurologicalDiagnosisYear", value)
        }
      />

      <Separator />

      <CerebrovascularHistory
        hasHadStroke={formData.hasHadStroke}
        strokeYear={formData.strokeYear}
        hasHadTIA={formData.hasHadTIA}
        tiaYear={formData.tiaYear}
        hasMultipleStrokes={formData.hasMultipleStrokes}
        multipleStrokesYear={formData.multipleStrokesYear}
        onStrokeChange={(value) => updateFormData("hasHadStroke", value)}
        onStrokeYearChange={(value) => updateFormData("strokeYear", value)}
        onTIAChange={(value) => updateFormData("hasHadTIA", value)}
        onTIAYearChange={(value) => updateFormData("tiaYear", value)}
        onMultipleStrokesChange={(value) =>
          updateFormData("hasMultipleStrokes", value)
        }
        onMultipleStrokesYearChange={(value) =>
          updateFormData("multipleStrokesYear", value)
        }
      />
      <Separator />

      <HeadInjury
        value={formData.hasHeadInjury}
        lossOfConsciousness={formData.headInjuryLossOfConsciousness}
        year={formData.headInjuryYear}
        onChange={(value) => updateFormData("hasHeadInjury", value)}
        onLossOfConsciousnessChange={(value) =>
          updateFormData("headInjuryLossOfConsciousness", value)
        }
        onYearChange={(value) => updateFormData("headInjuryYear", value)}
      />
      <Separator />

      <CardiovascularRiskFactors
        hasHypertension={formData.hasHypertension}
        hypertensionYear={formData.hypertensionYear}
        hasDiabetes={formData.hasDiabetes}
        diabetesYear={formData.diabetesYear}
        hasHighCholesterol={formData.hasHighCholesterol}
        highCholesterolYear={formData.highCholesterolYear}
        hasHeartDisease={formData.hasHeartDisease}
        heartDiseaseYear={formData.heartDiseaseYear}
        onHypertensionChange={(value) =>
          updateFormData("hasHypertension", value)
        }
        onHypertensionYearChange={(value) =>
          updateFormData("hypertensionYear", value)
        }
        onDiabetesChange={(value) => updateFormData("hasDiabetes", value)}
        onDiabetesYearChange={(value) => updateFormData("diabetesYear", value)}
        onHighCholesterolChange={(value) =>
          updateFormData("hasHighCholesterol", value)
        }
        onHighCholesterolYearChange={(value) =>
          updateFormData("highCholesterolYear", value)
        }
        onHeartDiseaseChange={(value) =>
          updateFormData("hasHeartDisease", value)
        }
        onHeartDiseaseYearChange={(value) =>
          updateFormData("heartDiseaseYear", value)
        }
      />
      <Separator />

      <FamilyHistory
        value={formData.hasFamilyNeurodegenerative}
        relation={formData.familyRelation}
        otherRelation={formData.familyOtherRelation}
        onChange={(value) =>
          updateFormData("hasFamilyNeurodegenerative", value)
        }
        onRelationChange={(value) => updateFormData("familyRelation", value)}
        onOtherRelationChange={(value) =>
          updateFormData("familyOtherRelation", value)
        }
      />
      <Separator />

      <OnsetPattern
        value={formData.onsetPattern}
        onChange={(value) => updateFormData("onsetPattern", value)}
      />
    </div>
  );
};

export default Step2NeurologicalHistory;
